// The two-way adapter between local config storage and the sync protocol:
// reading local configs as SyncRecordDTOs (to push) and applying pulled
// records back into local storage (with the LWW guard). This is the only
// module that knows both worlds — the engine just moves batches. Ported from
// assetto-gallery's src/sync/collect.ts (_planning/referencia-assetto.md
// §6.3), collapsed to config-strike's single sync kind.
//
// SyncRecordDTO shape (matches sync_records / db/schema.js):
//   { kind: 'config-profile', recordId: config.id, payload: <config object> | null,
//     updatedAt: <ISO string, = config.updated_at>, deleted: boolean }
//
// A config-profile's `payload` is the FULL config object as stored locally
// (id, name, settings, updated_at, ...) — the server never interprets it,
// just stores/orders it (jsonb).

import { storageService } from '../storageService'
import { getBridge } from '../bridge'
import { getOutboxEntry, withRemoteApply } from './outbox'

export const KIND_CONFIG_PROFILE = 'config-profile'

async function findLocalConfig(recordId) {
    const configs = await storageService.getConfigs()
    return configs.find((c) => c.id === recordId) ?? null
}

// --- outgoing: one dirty outbox entry -> the record to push ---

/**
 * Resolves an outbox entry to the CURRENT state of its record. Returns null
 * when a non-deleted entry points at a config that no longer exists locally
 * (it was deleted afterwards — a `deleted` outbox entry for it exists or
 * will, and that one carries the tombstone).
 */
export async function collectRecord(entry) {
    const base = { kind: entry.kind, recordId: entry.recordId, updatedAt: entry.updatedAt, deleted: entry.deleted }
    if (entry.deleted) return { ...base, payload: null }
    if (entry.kind !== KIND_CONFIG_PROFILE) return null // unknown/retired kind, nothing to collect

    const config = await findLocalConfig(entry.recordId)
    if (!config) return null
    return { ...base, payload: config, updatedAt: config.updated_at || entry.updatedAt }
}

// --- outgoing: the ENTIRE local dataset (first sync after linking an account) ---

export async function collectAll() {
    const configs = await storageService.getConfigs()
    return configs
        .filter((c) => c && c.id)
        .map((config) => ({
            kind: KIND_CONFIG_PROFILE,
            recordId: config.id,
            payload: config,
            // Configs saved before the sync engine existed may not carry a real
            // updated_at — fall back to the epoch so a real server-side
            // edit/tombstone from another device always wins over this "just
            // background state" upload (see EXISTENCE_EPOCH in assetto).
            updatedAt: config.updated_at || new Date(0).toISOString(),
            deleted: false,
        }))
}

// --- incoming: apply one pulled record with the LWW guard ---

/** True if the incoming record changed local state. */
export async function applyIncoming(record) {
    if (record.kind !== KIND_CONFIG_PROFILE) return false

    // A pending local change for the same record that is at least as new
    // wins: it will be pushed and the server will fold it in. Also swallows
    // echoes of our own pushes, which come back with identical timestamps.
    const pending = await getOutboxEntry(record.kind, record.recordId)
    if (pending && pending.updatedAt >= record.updatedAt) return false

    const bridge = getBridge()
    if (!bridge) return false

    const local = await findLocalConfig(record.recordId)
    if (local && local.updated_at && local.updated_at >= record.updatedAt) return false
    if (!local && record.deleted) return false

    let changed = false
    await withRemoteApply(async () => {
        if (record.deleted) {
            const res = await bridge.deleteLocalConfig({ id: record.recordId })
            changed = !!res?.success
            return
        }
        const payload = { ...record.payload, id: record.recordId, updated_at: record.updatedAt }
        const res = await bridge.saveLocalConfig(payload)
        changed = !!res?.success
    })
    return changed
}
